import React, { memo } from 'react';
import { StatName, StatsValidation, validateStatValue } from '../../utils/statsValidation';

export interface StatInputProps {
  /** The name of the stat being edited */
  statName: StatName;
  /** Display label for the stat */
  label: string;
  /** Current value of the stat */
  value: number;
  /** Base value for comparison (from character's base stats) */
  baseValue: number;
  /** Whether the input is editable or read-only */
  isEditable: boolean;
  /** Callback when value changes */
  onChange: (value: number) => void;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * StatInput Component
 * 
 * A numeric input component for individual stat editing with:
 * - Horizontal layout with label, base value, buttons, and current value
 * - Increment/decrement buttons (-10, -1, +1, +10)
 * - Red styling for decrement buttons, green for increment
 * - Display of current value and base value for comparison
 * - Editable/read-only mode based on props
 * 
 * Requirements: 4.1, 4.2, 4.6
 */
const StatInputComponent: React.FC<StatInputProps> = ({
  statName,
  label,
  value,
  baseValue,
  isEditable,
  onChange,
  className = '',
}) => {
  const bounds = StatsValidation[statName];

  const handleIncrement = (amount: number) => {
    const newValue = value + amount;
    if (validateStatValue(statName, newValue)) {
      onChange(newValue);
    } else {
      // Clamp to bounds
      const clampedValue = Math.max(bounds.min, Math.min(bounds.max, newValue));
      if (clampedValue !== value) {
        onChange(clampedValue);
      }
    }
  };

  const canDecrement = (amount: number) => value + amount >= bounds.min;
  const canIncrement = (amount: number) => value + amount <= bounds.max;

  return (
    <div className={`stat-input ${className}`}>
      <div className="flex items-center gap-3">
        {/* Label and base value */}
        <div className="w-24 flex-shrink-0">
          <label 
            className="text-sm font-semibold text-gray-200 block"
            htmlFor={`stat-${statName}`}
          >
            {label}
          </label>
          <span className="text-xs text-gray-500">Base: {baseValue}</span>
        </div>
        
        {isEditable ? (
          <div className="flex items-center gap-1.5 flex-1">
            {/* Decrement buttons (red) */}
            <button
              type="button"
              onClick={() => handleIncrement(-10)}
              disabled={!canDecrement(-10)}
              className={`
                w-9 h-9 rounded-lg text-xs font-bold
                transition-all duration-200 ease-out
                focus:outline-none focus:ring-2 focus:ring-red-500/50
                shadow-sm hover:shadow active:scale-95
                ${canDecrement(-10)
                  ? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white border border-red-400/50'
                  : 'bg-gray-700/50 text-gray-500 border border-gray-600 cursor-not-allowed'
                }
              `}
              aria-label={`Decrease ${label} by 10`}
            >
              -10
            </button>
            <button
              type="button"
              onClick={() => handleIncrement(-1)}
              disabled={!canDecrement(-1)}
              className={`
                w-9 h-9 rounded-lg text-sm font-bold
                transition-all duration-200 ease-out
                focus:outline-none focus:ring-2 focus:ring-red-500/50
                shadow-sm hover:shadow active:scale-95
                ${canDecrement(-1)
                  ? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white border border-red-400/50'
                  : 'bg-gray-700/50 text-gray-500 border border-gray-600 cursor-not-allowed'
                }
              `}
              aria-label={`Decrease ${label} by 1`}
            >
              -1
            </button>

            {/* Current value display */}
            <div
              id={`stat-${statName}`}
              className="
                w-16 px-2 py-2
                bg-gray-900/80 border-2 border-gray-600 rounded-lg
                text-white text-center font-bold text-lg
                shadow-inner
              "
              aria-label={`${label}: ${value}`}
            >
              {value}
            </div>

            {/* Increment buttons (green) */}
            <button
              type="button"
              onClick={() => handleIncrement(1)}
              disabled={!canIncrement(1)}
              className={`
                w-9 h-9 rounded-lg text-sm font-bold
                transition-all duration-200 ease-out
                focus:outline-none focus:ring-2 focus:ring-green-500/50
                shadow-sm hover:shadow active:scale-95
                ${canIncrement(1)
                  ? 'bg-gradient-to-b from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white border border-green-400/50'
                  : 'bg-gray-700/50 text-gray-500 border border-gray-600 cursor-not-allowed'
                }
              `}
              aria-label={`Increase ${label} by 1`}
            >
              +1
            </button>
            <button
              type="button"
              onClick={() => handleIncrement(10)}
              disabled={!canIncrement(10)}
              className={`
                w-9 h-9 rounded-lg text-xs font-bold
                transition-all duration-200 ease-out
                focus:outline-none focus:ring-2 focus:ring-green-500/50
                shadow-sm hover:shadow active:scale-95
                ${canIncrement(10)
                  ? 'bg-gradient-to-b from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white border border-green-400/50'
                  : 'bg-gray-700/50 text-gray-500 border border-gray-600 cursor-not-allowed'
                }
              `}
              aria-label={`Increase ${label} by 10`}
            >
              +10
            </button>
          </div>
        ) : (
          <div
            id={`stat-${statName}`}
            className="
              w-16 px-2 py-2 
              bg-gray-800/50 border border-gray-700 rounded-lg
              text-gray-300 text-center font-bold text-lg
            "
            aria-label={`${label}: ${value}`}
          >
            {value}
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize to prevent re-renders when other stats change
export const StatInput = memo(StatInputComponent);
